/**
 * TrendCardDetailPage — Individual CMF collection card sub-page
 *
 * Route: /trend/:trendSlug/:cardSlug
 *
 * PAGE STRUCTURE:
 *   1. Breadcrumb   — Home / Collection / Card title
 *   2. Back button  — link Back to previous page page
 *   3. TrendHero    — cinematic hero with description + tags + badge
 *   4. Material Samples section — scroll-reveal list of MaterialSampleCard
 *   5. 404 fallback
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { MaterialSampleCard } from "./components/MaterialSampleCard";
import { TrendHero } from "./components/TrendHero";
import { type MaterialGroup, trendData } from "./trendData";
import "./TrendCardDetailPage.css";

export const TrendCardDetailPage = () => {
  const { trendSlug, cardSlug } = useParams<{
    trendSlug: string;
    cardSlug: string;
  }>();
  const { t } = useTranslation();

  const entry = trendSlug ? trendData[trendSlug] : undefined;
  const detail = entry && cardSlug ? entry.cardDetails[cardSlug] : undefined;

  const sectionIndex = entry
    ? entry.sections.findIndex((s) => s.cards.some((c) => c.slug === cardSlug))
    : -1;
  const backTo = trendSlug
    ? `/trend/${trendSlug}${sectionIndex > 0 ? `?section=${sectionIndex}` : ""}`
    : "/";

  // ── 404 fallback ──────────────────────────────────────────────────────────
  if (!entry || !detail) {
    return (
      <div className="trend-card-detail-page trend-card-detail-page__not-found">
        <div className="trend-card-detail-page__not-found-accent" />
        <p className="trend-card-detail-page__not-found-title">
          {t("trend_coming_soon", "Detail page coming soon")}
        </p>
        <p className="trend-card-detail-page__not-found-desc">
          {t(
            "trend_coming_soon_desc",
            "The material detail for this card is being curated. Visit the collection to explore related grades.",
          )}
        </p>
        <Link to={backTo} className="trend-card-detail-page__not-found-link">
          ←{" "}
          {t("trend_back_to_collection", "Back to {{title}}", {
            title: entry?.title ?? "collection",
          })}
        </Link>
      </div>
    );
  }

  return (
    <div className="trend-card-detail-page">
      {/* Breadcrumb */}
      <nav
        className="trend-card-detail-page__breadcrumb"
        aria-label="Breadcrumb"
      >
        <Link
          to="/"
          className="trend-card-detail-page__crumb trend-card-detail-page__crumb--home"
        >
          <svg
            width="12"
            height="15"
            viewBox="0 0 12 14.414"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1 5.207 6 1l5 4.207v7.207a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V5.207Z"
              stroke="currentColor"
              strokeWidth="1.2"
            />
          </svg>
          {t("common_homepage", "Homepage")}
        </Link>
        <span className="trend-card-detail-page__crumb-sep" aria-hidden="true">
          /
        </span>
        <Link to={backTo} className="trend-card-detail-page__crumb">
          {entry.title}
        </Link>
        <span className="trend-card-detail-page__crumb-sep" aria-hidden="true">
          /
        </span>
        <span className="trend-card-detail-page__crumb trend-card-detail-page__crumb--active">
          {detail.title}
        </span>
      </nav>

      {/* Back link */}
      <div className="trend-card-detail-page__back-wrap">
        <Link to={backTo} className="trend-card-detail-page__back">
          <svg
            width="13"
            height="13"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M11 7H3M6.5 3.5L3 7l3.5 3.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="trend-card-detail-page__back-label">
            {t("trend_back_to_collection", "Back to {{title}}", {
              title: entry.title,
            })}
          </span>
        </Link>
      </div>

      {/* Cinematic hero */}
      <TrendHero
        heroImage={detail.heroImage}
        title={detail.title}
        chip={detail.collection}
        description={detail.heroQuote ?? detail.description}
        swatches={detail.heroSwatches}
        attribution={detail.heroAttribution}
        tags={detail.heroSwatches ? undefined : detail.tags}
        badge={detail.badge}
        tagLabel={entry.tagLabel ?? "CMF Collection"}
        showExplore
        exploreLabel={detail.heroQuote ? "View Materials" : "Scroll to Section"}
        exploreVariant="outline"
        onExplore={() =>
          document
            .getElementById("material-samples")
            ?.scrollIntoView({ behavior: "smooth" })
        }
        collapsible
      />

      {/* About this theme — collection editorial copy, only for entries
          that moved their body copy out of the hero (heroQuote present) */}
      {detail.heroQuote && (
        <section className="trend-card-detail-page__about-section">
          <h2 className="trend-card-detail-page__about-heading">
            {t("trend_about_theme", "About this theme")}
          </h2>
          <p className="trend-card-detail-page__about-text">
            {detail.description}
          </p>
        </section>
      )}

      {/* CMF Description — material/color rationale */}
      {detail.cmfDescription && (
        <section className="trend-card-detail-page__cmf-section">
          <h2 className="trend-card-detail-page__cmf-heading">CMF</h2>
          <p className="trend-card-detail-page__cmf-text">
            {detail.cmfDescription}
          </p>
        </section>
      )}

      {/* Two-column subsections (e.g. "Warm Embrace" / "Oasis of Freedom") */}
      {detail.subsections && detail.subsections.length > 0 && (
        <section className="trend-card-detail-page__subsections">
          <div className="trend-card-detail-page__subsections-grid">
            {detail.subsections.map((sub) => (
              <div
                key={sub.title}
                className="trend-card-detail-page__subsection"
              >
                <h3 className="trend-card-detail-page__subsection-title">
                  {sub.title}
                </h3>
                <p className="trend-card-detail-page__subsection-text">
                  {sub.text}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
      <MaterialSamplesSection
        samples={detail.materialSamples}
        productRender={detail.productRender}
        materialGroups={detail.materialGroups}
        heading={t("trend_material_samples", "Material Samples")}
        checkItNowLabel={t("trend_check_it_now", "Check it now")}
      />
    </div>
  );
};

// ── Material Samples Section ──────────────────────────────────────────────────

const MaterialSamplesSection = ({
  samples,
  productRender,
  materialGroups,
  heading,
  checkItNowLabel,
}: {
  samples: {
    image?: string;
    colorHex: string;
    name: string;
    ralColor: string;
    material: string;
    shape: string;
    appearance: string;
    productId?: string;
    searchUrl?: string;
  }[];
  productRender?: string;
  materialGroups?: MaterialGroup[];
  heading: string;
  checkItNowLabel: string;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.06 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const totalCount = materialGroups
    ? materialGroups.reduce((sum, g) => sum + g.samples.length, 0)
    : samples.length;

  if (materialGroups && materialGroups.length > 0) {
    return (
      <section
        id="material-samples"
        className="trend-card-detail-page__samples"
      >
        <div className="trend-card-detail-page__samples-header">
          <h2 className="trend-card-detail-page__samples-title">
            {heading}
            <span className="trend-card-detail-page__samples-count">
              {totalCount} {totalCount === 1 ? "color" : "colors"}
            </span>
          </h2>
        </div>

        <div
          ref={sectionRef}
          className="trend-card-detail-page__samples-groups"
        >
          {materialGroups.map((group, idx) => (
            <div
              key={`group-${idx}`}
              className={`trend-card-detail-page__samples-card${revealed ? " trend-card-detail-page__samples-card--visible" : ""}`}
            >
              <div className="trend-card-detail-page__samples-card-image">
                <img
                  src={group.image}
                  alt={`Material group ${idx + 1}`}
                  loading="lazy"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='400' height='400' fill='%23f0f0f0'/%3E%3C/svg%3E";
                  }}
                />
              </div>
              <div className="trend-card-detail-page__samples-card-colors">
                {group.samples.map((sample) => (
                  <MaterialSampleCard
                    key={sample.name}
                    {...sample}
                    compact
                    checkItNowLabel={checkItNowLabel}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const hasSharedImage = Boolean(productRender);

  if (hasSharedImage) {
    return (
      <section
        id="material-samples"
        className="trend-card-detail-page__samples"
      >
        <div className="trend-card-detail-page__samples-header">
          <h2 className="trend-card-detail-page__samples-title">
            {heading}
            <span className="trend-card-detail-page__samples-count">
              {samples.length} {samples.length === 1 ? "color" : "colors"}
            </span>
          </h2>
        </div>

        <div
          ref={sectionRef}
          className={`trend-card-detail-page__samples-card${revealed ? " trend-card-detail-page__samples-card--visible" : ""}`}
        >
          <div className="trend-card-detail-page__samples-card-image">
            <img
              src={productRender}
              alt="Product render"
              loading="lazy"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='400' height='400' fill='%23f0f0f0'/%3E%3C/svg%3E";
              }}
            />
          </div>
          <div className="trend-card-detail-page__samples-card-colors">
            {samples.map((sample) => (
              <MaterialSampleCard
                key={sample.name}
                {...sample}
                compact
                checkItNowLabel={checkItNowLabel}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="material-samples" className="trend-card-detail-page__samples">
      <div className="trend-card-detail-page__samples-header">
        <h2 className="trend-card-detail-page__samples-title">
          {heading}
          <span className="trend-card-detail-page__samples-count">
            {samples.length} {samples.length === 1 ? "color" : "colors"}
          </span>
        </h2>
      </div>

      <div ref={sectionRef} className="trend-card-detail-page__samples-list">
        {samples.map((sample, i) => (
          <div
            key={`${sample.name}-${i}`}
            className={`trend-card-detail-page__sample-item${revealed ? " trend-card-detail-page__sample-item--visible" : ""}`}
            data-idx={i}
          >
            <MaterialSampleCard {...sample} checkItNowLabel={checkItNowLabel} />
          </div>
        ))}
      </div>
    </section>
  );
};
